// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Button, Center, Divider, Group, Indicator, List, Space, Text, useMantineTheme } from '@mantine/core';
import { IconCheck } from '@tabler/icons';
import { FC, useContext, useMemo } from 'react';
import PanePaper from '../../../Molecules/PanePaper';
import { ChargebeeS } from '../../../Service/ChargebeeS';
import { AuthContext, useJwtClaims } from '../../../Service/Context/AuthContext';
import { BillingInterval, BillingPlan } from '../../../Types/Billing';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { LicenceS } from '../../../Service/LicenceS';
import { BookitupSubscriptionContext } from '../../../Service/Context/BookitupSubscriptionContext';
import { useAppUser } from '../../../Service/Context/ApplicationUserContext';

interface Props {
  billingPlan: BillingPlan;
  billingInterval: BillingInterval;
}

const ACTIVE_BILLING_COLOR = '#FE8047';

const Plan: FC<Props> = ({ billingPlan, billingInterval }) => {
  const { refreshToken } = useContext(AuthContext);
  const jwtClaims = useJwtClaims();
  const { origin } = useAppUser();
  const { subscription, refresh: refreshSubscription, resourceAddon } = useContext(BookitupSubscriptionContext);
  const { refresh: refreshAddon } = useContext(ResourceAddonContext);
  const { name, internalName, free, price, features, licence, description } = billingPlan;

  const { status, licence: subscriptionLicence, interval: subscriptionInterval } = subscription ?? {};
  const planIncompatible = resourceAddon && resourceAddon.interval !== billingInterval;
  const subscriptionExists = subscriptionLicence !== undefined && status !== 'CANCELLED';
  const isPlanActive = status !== 'CANCELLED' && licence === subscriptionLicence;
  const isCurrentBillingInterval = billingInterval === subscriptionInterval;
  const canChangeBillingInterval = isPlanActive && !isCurrentBillingInterval && licence !== 'BASIC';
  const showPlanCheckoutButton = (!isPlanActive || canChangeBillingInterval) && licence !== 'BASIC';
  const getPlanCheckoutLabel = () => {
    if (canChangeBillingInterval) return 'Abrechnungsintervall ändern';
    if (subscriptionExists) return 'Plan ändern';
    return 'Plan buchen';
  };

  const { colors, colorScheme } = useMantineTheme();

  const discount = useMemo(
    () => (billingInterval === 'Yearly' ? LicenceS.getAvailableDiscount(jwtClaims, name, origin) : null),
    [billingInterval, jwtClaims, name, origin],
  );

  const createOrUpdateSubscription = (coupon?: string) => {
    const planId = `${internalName}-EUR-${billingInterval}`;
    const onSuccess = () => {
      refreshToken();
      refreshAddon();
      refreshSubscription();
    };
    ChargebeeS.openHostedCheckout(planId, onSuccess, billingPlan, coupon);
  };

  const card = (
    <PanePaper
      sx={{
        width: '100%',
        height: '100%',
        border: isPlanActive
          ? `3px solid ${ACTIVE_BILLING_COLOR}`
          : `1px solid ${colorScheme === 'dark' ? colors.dark[4] : colors.gray[1]}`,
      }}
    >
      <Group spacing="sm" noWrap sx={{ minHeight: 36 }}>
        <Text weight={700} size="xl">
          {name}
        </Text>
      </Group>
      <Text color="dimmed" mt="xs" size="xs">
        {description}
      </Text>
      <Space h="xl" />
      {free ? (
        <Text align="center" size="lg" mb="xl">
          Kostenlos
        </Text>
      ) : (
        <>
          {!discount && <Text align="center" weight={800} size={22}>{`${price[billingInterval]} € / Monat`}</Text>}
          {discount && (
            <DiscountPrice
              price={price[billingInterval]}
              name={discount.dealName}
              discountPercentage={discount.discountPercentage}
            />
          )}
          <Text align="center" size="xs" color="dimmed">
            zzgl. gesetzl. MwSt.
          </Text>
        </>
      )}
      {!planIncompatible && (
        <Center mt="xl" mb="xl">
          {showPlanCheckoutButton && (
            <Button variant="outline" onClick={() => createOrUpdateSubscription(discount?.coupon)}>
              {getPlanCheckoutLabel()}
            </Button>
          )}
        </Center>
      )}
      <Divider my="md" />
      <Text size="sm" weight="bolder" mt={planIncompatible ? 'xl' : 0}>
        Bellebte Funktionen:
      </Text>
      <Space h="sm" />
      <List spacing={5} size="xs" icon={<IconCheck size={12} stroke={3} color="#FDAF47" />}>
        {/* {topFeaturesComponent && topFeaturesComponent} */}
        {features.map((feature) => (
          <List.Item key={feature}>{feature}</List.Item>
        ))}
      </List>
    </PanePaper>
  );

  return (
    <Indicator
      disabled={!isPlanActive}
      label={
        <Text color="white" size={11} weight="bold">
          {isPlanActive ? 'Aktiv' : 'Neu'}
        </Text>
      }
      position="top-center"
      radius="md"
      size={18}
      styles={(theme) => ({
        indicator: {
          backgroundColor: ACTIVE_BILLING_COLOR,
          color: theme.white,
          width: 50,
        },
      })}
      sx={{
        flex: '1 1 250px',
        maxWidth: 250,
        width: '100%',
        '@media (max-width: 576px)': {
          maxWidth: '100%',
        },
      }}
    >
      {card}
    </Indicator>
  );
};

const DiscountPrice: FC<{ price: number; name: string; discountPercentage: number }> = ({
  price,
  name,
  discountPercentage,
}) => (
  <Center sx={{ borderRadius: 10 }}>
    <Box sx={{ width: 175 }}>
      <Text
        py={2}
        align="center"
        size="xs"
        weight="bolder"
        sx={{ backgroundColor: '#FEE956', borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
        color="black"
      >
        {name}
      </Text>
      <Box py={5} sx={{ backgroundColor: 'black', borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
        <Text my={5} color="white" align="center" size="lg">
          {`${price * (1 - discountPercentage / 100)} € / Monat`}
        </Text>
        <Text align="center" size="sm" color="white" td="line-through">{`${price} € / Monat`}</Text>
      </Box>
    </Box>
  </Center>
);

export default Plan;
